'use client';

import { motion } from 'framer-motion';
import { PortableText } from '@portabletext/react';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { CTAButton } from '@/shadcn/ui/button';
import { PageAboutUsSchema } from '@/sanity/pages/PageAboutUsTemplate';
import { GroqImage } from '@/helpers/Image';
import Image from 'next/image';
import heroMobileBg from '@/public/about-us/about-us-hero-mobile-bg.png';
import { PageWhyPropTradingSchema } from '@/sanity/pages/PageWhyPropTradingTemplate';

const ease = [0.25, 0.1, 0.25, 1] as const;

export default function HeroSection({
  data,
}: {
  data: PageWhyPropTradingSchema;
}) {
  return (
    <section className="relative w-full overflow-x-clip min-h-[90rem] lg:min-h-[80.5rem] lg:justify-center flex flex-col ">
      {/* Background — mobile */}
      <motion.div
        initial={{ opacity: 0, filter: 'blur(8px)' }}
        animate={{ opacity: 1, filter: 'blur(0px)' }}
        transition={{ duration: 1.2, ease }}
        className="absolute inset-0 w-full h-full pointer-events-none block lg:hidden"
        aria-hidden="true"
      >
        <Image
          src={heroMobileBg}
          alt=""
          className="w-full h-full object-cover object-center"
        />
      </motion.div>

      {/* Background — desktop */}
      {data?.heroBackgroundImage && (
        <div
          className="absolute w-full h-full inset-0 pointer-events-none hidden lg:block"
          aria-hidden="true"
        >
          <motion.div
            initial={{ opacity: 0, filter: 'blur(8px)' }}
            animate={{ opacity: 1, filter: 'blur(0px)' }}
            transition={{ duration: 1.2, ease }}
            className="w-full h-full"
          >
            <GroqImage
              src={data?.heroBackgroundImage}
              alt=""
              className="!w-full !h-full !object-cover"
            />
          </motion.div>
        </div>
      )}

      <div className="pt-[7rem] lg:py-[1.1rem] flex items-center w-[min(140rem,calc(100%-4rem))] lg:w-[min(140rem,calc(100%-8rem))] mx-auto relative">
        {/* Content */}
        <div className="relative z-10 flex flex-col gap-[3rem] lg:gap-[2.63rem] lg:items-start items-center w-full lg:max-w-[85rem] ">
          <div className="flex flex-col gap-[1.75rem] lg:items-start items-center">
            {data?.heroChip && (
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease, delay: 0.2 }}
                className="mb-0 font-blacker italic text-[2rem] text-black leading-[1.5]"
              >
                {data.heroChip}
              </motion.p>
            )}

            {/* Desktop title */}
            {data?.heroTitle && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease, delay: 0.35 }}
                className="richtext lg:richtext-start prose-p:font-medium gap-[2rem] [&_*]:[text-wrap:unset] prose-headings:lg:text-[7rem] prose-headings:text-[5.25rem]"
              >
                <PortableText
                  value={data.heroTitle}
                  components={richTextComponent}
                />
              </motion.div>
            )}
          </div>

          {data?.heroCTA && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease, delay: 0.5 }}
              className="w-full lg:w-fit"
            >
              <CTAButton data={data.heroCTA} />
            </motion.div>
          )}
        </div>
      </div>
      {/* Person image */}
      {data?.heroImage && (
        <div
          className="absolute  lg:right-[30rem] right-auto lg:left-auto left-1/2 lg:-translate-x-0 -translate-x-1/2 bottom-0  overflow-clip lg:w-[74.875rem] w-[50rem]  h-auto pointer-events-none z-10"
          aria-hidden="true"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, ease, delay: 0.3 }}
            className="w-full h-full"
          >
            <GroqImage
              src={data.heroImage}
              alt={data.heroImage.name || ''}
              className="object-contain object-top"
            />
          </motion.div>
        </div>
      )}
    </section>
  );
}
